import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, BookOpen, Clock, Brain, DollarSign } from 'lucide-react';
import { BonusCard } from '../components/BonusCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchaseNotification } from '../components/PurchaseNotification';
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  return <div className="min-h-screen bg-[#F9F7F2] font-sans text-[#1F2937] overflow-x-hidden" data-id="element-25">
      <PurchaseNotification data-id="element-26" />

      {/* Hero Section */}
      <header className="relative bg-[#2C5F2D] text-[#F9F7F2] pt-16 pb-24 px-4 overflow-hidden" data-id="element-27">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/paper.png')]" data-id="element-28"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-29">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-30">
            <span className="inline-block px-4 py-1 bg-[#D97706] text-white text-sm font-bold tracking-wider rounded-full mb-6 uppercase" data-id="element-31">
              Sistema de Organización
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-32">
              No es cocinar. <br data-id="element-33" />
              <span className="text-[#FCD34D] italic" data-id="element-34">Es decidir.</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#E5E7EB] mb-10 max-w-2xl mx-auto font-light" data-id="element-35">
              El sistema simple para comer mejor, gastar menos y dejar de pensar
              todos los días qué vas a comer
            </p>
            <button onClick={scrollToPurchase} className="bg-[#D97706] hover:bg-[#B45309] text-white font-bold py-4 px-8 rounded-lg text-lg shadow-lg transform transition hover:-translate-y-1 hover:shadow-xl flex items-center mx-auto gap-2" data-id="element-36">
              Quiero dejar de decidir
              <ArrowRight className="w-5 h-5" data-id="element-37" />
            </button>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none" data-id="element-38">
          <svg className="relative block w-[calc(100%+1.3px)] h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-39">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-[#F9F7F2]" data-id="element-40"></path>
          </svg>
        </div>
      </header>

      {/* Main Content Container */}
      <main className="max-w-3xl mx-auto px-4 py-12 md:py-20" data-id="element-41">
        {/* Problem Section */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="mb-20" data-id="element-42">
          <motion.div variants={fadeIn} className="prose prose-lg prose-stone mx-auto" data-id="element-43">
            <h2 className="font-serif text-3xl font-bold text-[#2C5F2D] mb-6" data-id="element-44">
              No estás cansado de cocinar. Estás cansado de decidir.
            </h2>
            <p className="text-lg leading-relaxed mb-6" data-id="element-45">
              Decidir qué comer hoy. Decidir si vas al supermercado o pedís
              algo. Decidir qué comprar. Decidir qué hacer con lo que sobró.
              Decidir otra vez mañana. Y pasado. Y el resto del año.
            </p>
            <p className="text-lg leading-relaxed mb-6 font-medium bg-[#E6F4EA] p-4 rounded-l-4 border-l-4 border-[#2C5F2D]" data-id="element-46">
              Esa pregunta tan simple — “¿Qué comemos hoy?” — se repite 365
              veces al año.
            </p>
            <p className="text-lg leading-relaxed mb-6" data-id="element-47">
              Y cada vez te quita algo: energía, dinero, paciencia y sensación
              de control.
            </p>
            <p className="text-lg leading-relaxed font-bold text-[#D97706]" data-id="element-48">
              El problema nunca fue la comida. El problema es el cansancio
              mental constante.
            </p>
          </motion.div>
        </motion.section>

        {/* Agitation Section */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-20 bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-[#E5E7EB]" data-id="element-49">
          <h3 className="font-serif text-2xl font-bold text-[#1F2937] mb-6 flex items-center gap-3" data-id="element-50">
            <Brain className="text-[#D97706]" data-id="element-51" />
            El problema que nadie te explicó
          </h3>
          <p className="mb-4" data-id="element-52">Tal vez te pasa esto:</p>
          <ul className="space-y-3 mb-8" data-id="element-53">
            {['Abrís la heladera y no sabés qué hacer con lo que hay', 'Vas al supermercado sin una idea clara', 'Comprás “por las dudas”', 'Tirás comida que se echó a perder', 'Pedís delivery por cansancio, no por gusto', 'Gastás más de lo que querés', 'Terminás el día con culpa'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-54">
                <div className="mt-1.5 min-w-[6px] h-[6px] rounded-full bg-[#D97706]" data-id="element-55" />
                <span data-id="element-56">{item}</span>
              </li>)}
          </ul>

          <div className="bg-[#F3F4F6] p-6 rounded-lg italic text-gray-600 mb-6 border-l-4 border-gray-400" data-id="element-57">
            “Debería organizarme mejor…” “Debería comer más sano…” “Mañana
            empiezo…”
          </div>

          <p className="font-bold" data-id="element-58">
            Pero mañana vuelve la misma pregunta. Y el ciclo se repite.
          </p>
        </motion.section>

        {/* Solution Section */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-20" data-id="element-59">
          <h2 className="font-serif text-3xl font-bold text-[#2C5F2D] mb-8 text-center" data-id="element-60">
            La verdad incómoda
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-61">
            <div data-id="element-62">
              <p className="mb-4 text-lg" data-id="element-63">
                El problema no es qué comés. El problema es que decidís todo
                desde cero, todos los días.
              </p>
              <p className="mb-4 text-lg" data-id="element-64">
                Cada decisión consume energía mental. Y la energía mental se
                agota.
              </p>
              <p className="mb-4 text-lg font-medium" data-id="element-65">
                Cuando se agota, improvisás. Y improvisar es caro.
              </p>
            </div>
            <div className="bg-[#2C5F2D] text-white p-6 rounded-xl shadow-lg transform rotate-1" data-id="element-66">
              <ul className="space-y-4" data-id="element-67">
                <li className="flex items-center gap-3" data-id="element-68">
                  <DollarSign className="text-[#FCD34D]" data-id="element-69" /> Caro en dinero
                </li>
                <li className="flex items-center gap-3" data-id="element-70">
                  <Clock className="text-[#FCD34D]" data-id="element-71" /> Caro en tiempo
                </li>
                <li className="flex items-center gap-3" data-id="element-72">
                  <Check className="text-[#FCD34D]" data-id="element-73" /> Caro en salud
                </li>
                <li className="flex items-center gap-3" data-id="element-74">
                  <Brain className="text-[#FCD34D]" data-id="element-75" /> Caro en tranquilidad
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-xl font-serif italic text-gray-600 mb-12" data-id="element-76">
            Nadie te enseñó a gestionar decisiones. Solo te dieron recetas.
          </p>
        </motion.section>

        {/* Product Reveal */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-24 text-center" data-id="element-77">
          <div className="inline-block p-2 bg-[#E6F4EA] rounded-full mb-6" data-id="element-78">
            <Star className="w-8 h-8 text-[#2C5F2D] fill-current" data-id="element-79" />
          </div>
          <h2 className="font-serif text-4xl font-bold text-[#1F2937] mb-4" data-id="element-80">
            Esto no es un recetario
          </h2>
          <h3 className="text-2xl text-[#D97706] font-bold mb-8" data-id="element-81">
            Es un sistema
          </h3>

          <div className="relative max-w-md mx-auto mb-12 shadow-2xl rounded-lg overflow-hidden transform hover:scale-105 transition-transform duration-500" data-id="element-82">
            <img src="https://cdn.magicpatterns.com/uploads/6hZXoUcuUtkYSGvqor9a1X/cover.png" alt="No es cocinar. Es decidir. Ebook Cover" className="w-full h-auto" data-id="element-83" />
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md text-left" data-id="element-84">
            <p className="mb-6 text-lg" data-id="element-85">
              <span className="font-bold text-[#2C5F2D]" data-id="element-86">
                No es cocinar. Es decidir.
              </span>{' '}
              Es un ebook práctico que te enseña a crear tu propio sistema de
              comidas para que:
            </p>
            <ul className="space-y-4 mb-8" data-id="element-87">
              {['Decidas una sola vez y uses ese criterio todo el mes', 'Dejes de improvisar', 'Gastés menos sin sentirte limitado', 'Libres espacio mental todos los días', 'Recuperes orden, control y tranquilidad'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-88">
                  <Check className="w-5 h-5 text-[#2C5F2D] mt-1 flex-shrink-0" data-id="element-89" />
                  <span className="text-gray-700" data-id="element-90">{item}</span>
                </li>)}
            </ul>
            <p className="text-center font-bold text-xl text-[#D97706]" data-id="element-91">
              No te dice qué comer. Te enseña cómo decidir.
            </p>
          </div>
        </motion.section>

        {/* The Mechanism */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-20" data-id="element-92">
          <h2 className="font-serif text-3xl font-bold text-[#2C5F2D] mb-8" data-id="element-93">
            El mecanismo que faltaba: Decisiones predefinidas
          </h2>
          <p className="text-lg mb-8" data-id="element-94">
            Un sistema donde no pensás cada día. No debatís. No improvisás.
            Ejecutás.
          </p>

          <div className="grid gap-6 md:grid-cols-3" data-id="element-95">
            {[{
            title: 'Como tu ropa',
            desc: 'No pensás desde cero. Ya sabés qué funciona.',
            icon: '👕'
          }, {
            title: 'Como un GPS',
            desc: 'No discutís rutas. Seguís el camino.',
            icon: '📍'
          }, {
            title: 'Como una playlist',
            desc: 'No elegís canción por canción. Le das play.',
            icon: '🎵'
          }].map((item, i) => <div key={i} className="bg-[#F9F7F2] border-2 border-[#E5E7EB] p-6 rounded-xl text-center hover:border-[#D97706] transition-colors" data-id="element-96">
                <div className="text-4xl mb-4" data-id="element-97">{item.icon}</div>
                <h4 className="font-bold mb-2" data-id="element-98">{item.title}</h4>
                <p className="text-sm text-gray-600" data-id="element-99">{item.desc}</p>
              </div>)}
          </div>
        </motion.section>

        {/* Bonuses Section */}
        <section className="mb-24" data-id="element-100">
          <div className="text-center mb-12" data-id="element-101">
            <h2 className="font-serif text-3xl font-bold text-[#2C5F2D] mb-4" data-id="element-102">
              Regalos exclusivos si ordenás hoy
            </h2>
            <p className="text-gray-600" data-id="element-103">
              Herramientas prácticas para acelerar tus resultados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-104">
            <BonusCard title="Bono 1: El Mapa de Decisiones" description="Un esquema visual donde definís tus comidas base, criterios y límites. El PDF práctico de alto valor psicológico." imageUrl="https://cdn.magicpatterns.com/uploads/k9rNwqWnK7yhXwDbSYf4oN/bono_1.png" delay={0.1} data-id="element-105" />
            <BonusCard title="Bono 2: Plantilla 30 Días" description="Tu 'playlist de comidas'. Una plantilla simple que responde la pregunta antes de que aparezca. Editable e imprimible." imageUrl="https://cdn.magicpatterns.com/uploads/7g7tHQfq4j7Qwh43KdzkUv/bono_2.png" delay={0.2} data-id="element-106" />
            <BonusCard title="Bono 3: Sistema Antidelivery" description="Mini-guía para cuando estás cansado. Cómo decidir rápido y evitar pedir comida chatarra por agotamiento mental." imageUrl="https://cdn.magicpatterns.com/uploads/xkDcUVS9bFwMZTtFp5YwoC/bono_3.png" delay={0.3} data-id="element-107" />
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-24 bg-[#E6F4EA] -mx-4 md:-mx-20 px-4 md:px-20 py-16 rounded-3xl" data-id="element-108">
          <h2 className="font-serif text-3xl font-bold text-[#2C5F2D] mb-10 text-center" data-id="element-109">
            Lo que dicen quienes ya decidieron
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-110">
            <TestimonialCard name="María González" role="Madre trabajadora, 34 años" content="Sentía que cocinar era mi segundo trabajo. Con este sistema recuperé mis noches. Ya no discuto con mi marido sobre qué cenar." delay={0.1} data-id="element-111" />
            <TestimonialCard name="Carlos Ramírez" role="Profesional, 42 años" content="Soy muy estructurado en mi trabajo pero un desastre en la cocina. Esto me dio la estructura que necesitaba. Simple y lógico." delay={0.2} data-id="element-112" />
            <TestimonialCard name="Ana Martínez" role="Abogada, 29 años" content="Gastaba una fortuna en delivery por puro cansancio. En la primera semana el libro se pagó solo con lo que ahorré." delay={0.3} data-id="element-113" />
            <TestimonialCard name="Roberto Silva" role="Padre de dos, 38 años" content="No es una dieta, es orden mental. Dejé de tirar comida y por fin siento que controlo mi alimentación y no al revés." delay={0.4} data-id="element-114" />
            <TestimonialCard name="Laura Torres" role="Gerente, 45 años" content="Lo mejor es que no te obliga a cocinar cosas raras. Usás lo que ya sabés hacer pero de forma inteligente. Brillante." delay={0.5} data-id="element-115" />
          </div>
        </section>

        {/* Purchase Section */}
        <div id="purchase-section" className="scroll-mt-10" data-id="element-116">
          <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.5
        }} className="bg-white border-4 border-[#2C5F2D] rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden" data-id="element-117">
            <div className="absolute top-0 right-0 bg-[#D97706] text-white px-8 py-2 transform rotate-45 translate-x-8 translate-y-4 font-bold shadow-md" data-id="element-118">
              OFERTA ESPECIAL
            </div>

            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2937] mb-4" data-id="element-119">
              Empezá hoy. Dejá de pensar.
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-120">
              Menos decisiones. Más control.
            </p>

            <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-10" data-id="element-121">
              <div className="w-48 shadow-lg rotate-[-5deg] border-4 border-white rounded-lg" data-id="element-122">
                <img src="https://cdn.magicpatterns.com/uploads/6hZXoUcuUtkYSGvqor9a1X/cover.png" alt="Book Cover" className="w-full" data-id="element-123" />
              </div>
              <div className="text-left" data-id="element-124">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-125">
                  <span className="text-gray-400 line-through text-xl" data-id="element-126">
                    $29.990
                  </span>
                  <span className="text-5xl font-bold text-[#2C5F2D]" data-id="element-127">
                    $14.990
                  </span>
                </div>
                <p className="text-[#D97706] font-bold text-sm mb-6 uppercase tracking-wide" data-id="element-128">
                  + 3 Bonos de Regalo Incluidos
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-6" data-id="element-129">
                  <li className="flex items-center gap-2" data-id="element-130">
                    <Check className="w-4 h-4 text-[#2C5F2D]" data-id="element-131" /> Acceso
                    inmediato (Digital)
                  </li>
                  <li className="flex items-center gap-2" data-id="element-132">
                    <Check className="w-4 h-4 text-[#2C5F2D]" data-id="element-133" /> Pago único y
                    seguro
                  </li>
                  <li className="flex items-center gap-2" data-id="element-134">
                    <Check className="w-4 h-4 text-[#2C5F2D]" data-id="element-135" /> Garantía de
                    satisfacción
                  </li>
                </ul>
              </div>
            </div>

            <a href="https://checkout.tupuntodigital.online/checkouts/cn/hWN7BYzYfCquuZ6N9tODxPco/es-ar?_r=AQABNB9CxgoCBg5l8tWhRAF0f_7XV5BRM_siZjAr14ANGIw&preview_theme_id=147264340118" className="block w-full md:w-auto md:inline-block bg-[#D97706] hover:bg-[#B45309] text-white font-bold py-5 px-12 rounded-xl text-xl shadow-lg transform transition hover:-translate-y-1 hover:shadow-xl mb-6" data-id="element-136">
              QUIERO EL SISTEMA AHORA
            </a>

            <p className="text-xs text-gray-500 max-w-md mx-auto" data-id="element-137">
              Al hacer clic serás redirigido a la plataforma de pago segura.
              Recibirás el acceso a tu email inmediatamente después de la
              compra.
            </p>
          </motion.div>
        </div>

        {/* FAQ / Final CTA */}
        <div className="mt-20 text-center" data-id="element-138">
          <h3 className="font-serif text-2xl font-bold text-[#1F2937] mb-6" data-id="element-139">
            ¿Para quién es este sistema?
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700 leading-relaxed mb-8" data-id="element-140">
            Para adultos de 25 a 55 años. Para personas que trabajan y toman
            muchas decisiones al día. Para padres, madres o parejas que deciden
            qué se come. Para quienes están cansados de pensar. Para quienes
            buscan paz mental, orden y ahorro.
          </p>
          <p className="font-medium text-[#2C5F2D]" data-id="element-141">
            No es para chefs. No es para fanáticos de la nutrición. No es para
            quien quiere recetas gourmet.
          </p>
          <p className="mt-2 font-bold text-[#1F2937]" data-id="element-142">
            Es para gente real, con vida real.
          </p>
        </div>
      </main>

      <footer className="bg-[#1F2937] text-gray-400 py-12 text-center text-sm" data-id="element-143">
        <div className="max-w-4xl mx-auto px-4" data-id="element-144">
          <p className="mb-4" data-id="element-145">
            &copy; {new Date().getFullYear()} No es cocinar. Es decidir.
          </p>
          <p data-id="element-146">Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>;
}